/**
 * StructureInteractOverlay.js
 *
 * Phase 11 — Context-sensitive interaction overlay for structures the player
 * is standing next to. Behavior per type:
 *   - beacon       : informational only ("Beacon active")
 *   - storage_crate: deposit/withdraw ore (table with +/- buttons)
 *   - auto_miner   : show buffer + collect button
 *   - refuel_pad   : repair button (costs nanites)
 *   - turret       : show magazine + refill button
 *
 * All actions hit /api/structures/{id}/... and refresh inventory/nanites
 * through the onAfterAction callback supplied by the parent.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import {
  X, Package, Pickaxe, BatteryCharging, Crosshair, Radar, ArrowDown, ArrowUp,
  Trash2, RefreshCw, Sparkles, Wind, Zap, Lightbulb, Factory, Store, Database, Home,
} from 'lucide-react';
import { toast } from 'sonner';
import AlloySellPanel from './AlloySellPanel';

const TYPE_LABEL = {
  beacon: 'Marker Beacon',
  storage_crate: 'Storage Crate',
  auto_miner: 'Auto-Miner',
  refuel_pad: 'Refuel Pad',
  turret: 'Defense Turret',
  oxygen_generator: 'Oxygen Generator',
  power_generator: 'Power Generator',
  floodlight: 'Floodlight Tower',
  refinery: 'Refinery',
  trade_terminal: 'Trade Terminal',
  cargo_silo: 'Cargo Silo',
  heavy_turret: 'Heavy Turret',
  habitat: 'Habitat Module',
};

const TYPE_ICON = {
  beacon: Radar,
  storage_crate: Package,
  auto_miner: Pickaxe,
  refuel_pad: BatteryCharging,
  turret: Crosshair,
  oxygen_generator: Wind,
  power_generator: Zap,
  floodlight: Lightbulb,
  refinery: Factory,
  trade_terminal: Store,
  cargo_silo: Database,
  heavy_turret: Crosshair,
  habitat: Home,
};

export function StructureInteractOverlay({
  open,
  structure,         // doc { id, type, hp, max_hp, data: {...} }
  inventory,         // player inventory (for crate transfer UI)
  onClose,
  onAfterAction,     // (kind, payload) => void  — parent refreshes credits/inv
  onOpenCrafting,    // () => void — Phase 2: open the alloy Fabricator
}) {
  const { api } = useAuth();
  const [doc, setDoc] = useState(structure || null);
  const [busy, setBusy] = useState(false);

  // When the overlay opens (prop changes), seed from the scene's copy then
  // immediately pull the LATEST persisted state from the backend. The scene
  // holds an in-memory snapshot taken at planet load, so deposited refinery
  // slots / crate contents would otherwise appear empty on reopen.
  useEffect(() => {
    setDoc(structure || null);
    if (!structure?.id) return;
    api.get('/api/structures', {
      params: {
        coords_key: structure.coords_key,
        planet_idx: structure.planet_idx,
        moon_idx: structure.moon_idx ?? null,
      },
    }).then(({ data }) => {
      const fresh = (data?.structures || []).find((s) => s.id === structure.id);
      if (fresh) setDoc(fresh);
    }).catch(() => { /* noop */ });
  }, [structure, api]);

  const refreshStructure = useCallback(async () => {
    if (!doc) return;
    try {
      const { data } = await api.get('/api/structures', {
        params: {
          coords_key: doc.coords_key,
          planet_idx: doc.planet_idx,
          moon_idx: doc.moon_idx ?? null,
        },
      });
      const fresh = (data?.structures || []).find((s) => s.id === doc.id);
      if (fresh) setDoc(fresh);
    } catch (_e) { /* noop */ }
  }, [api, doc]);

  // ---------------- Per-type handlers ----------------

  const handleRefuel = useCallback(async () => {
    if (!doc) return;
    setBusy(true);
    try {
      await api.post(`/api/structures/${doc.id}/refuel`);
      toast.success('Refit complete');
      onAfterAction?.('refuel');
      await refreshStructure();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Refuel failed');
    } finally {
      setBusy(false);
    }
  }, [api, doc, onAfterAction, refreshStructure]);

  const handleCrateDeposit = useCallback(async (symbol, qty) => {
    if (!doc) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/api/structures/${doc.id}/crate-deposit`, { symbol, qty });
      setDoc(data?.structure || doc);
      toast.success(`Deposited ${qty}u ${symbol}`);
      onAfterAction?.('crate-deposit');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Deposit failed');
    } finally {
      setBusy(false);
    }
  }, [api, doc, onAfterAction]);

  const handleCrateWithdraw = useCallback(async (symbol, qty) => {
    if (!doc) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/api/structures/${doc.id}/crate-withdraw`, { symbol, qty });
      setDoc(data?.structure || doc);
      toast.success(`Withdrew ${qty}u ${symbol}`);
      onAfterAction?.('crate-withdraw');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Withdraw failed');
    } finally {
      setBusy(false);
    }
  }, [api, doc, onAfterAction]);

  const handleMinerCollect = useCallback(async () => {
    if (!doc) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/api/structures/${doc.id}/miner-collect`);
      setDoc(data?.structure || doc);
      toast.success(`Collected ${data?.collected?.toFixed(1)}u`);
      onAfterAction?.('miner-collect');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Collect failed');
    } finally {
      setBusy(false);
    }
  }, [api, doc, onAfterAction]);

  const handleTurretRefill = useCallback(async () => {
    if (!doc) return;
    setBusy(true);
    try {
      await api.post(`/api/structures/${doc.id}/turret-refill`);
      toast.success('Turret reloaded');
      onAfterAction?.('turret-refill');
      await refreshStructure();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Refill failed');
    } finally {
      setBusy(false);
    }
  }, [api, doc, onAfterAction, refreshStructure]);

  const handlePowerCollect = useCallback(async () => {
    if (!doc) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/api/structures/${doc.id}/power-collect`);
      setDoc(data?.structure || doc);
      toast.success(data?.collected > 0 ? `Collected ${data.collected} nanites` : 'Nothing accrued yet');
      onAfterAction?.('power-collect');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Collect failed');
    } finally {
      setBusy(false);
    }
  }, [api, doc, onAfterAction]);

  const handleRefineDeposit = useCallback(async (symbol, qty) => {
    if (!doc) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/api/structures/${doc.id}/refine-deposit`, { symbol, qty });
      setDoc(data?.structure || doc);
      toast.success(`Refining ${qty}u ${symbol}`);
      onAfterAction?.('refine-deposit');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Deposit failed');
    } finally {
      setBusy(false);
    }
  }, [api, doc, onAfterAction]);

  const handleRefineCollect = useCallback(async () => {
    if (!doc) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/api/structures/${doc.id}/refine-collect`);
      setDoc(data?.structure || doc);
      toast.success('Refined ore collected');
      onAfterAction?.('refine-collect');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Collect failed');
    } finally {
      setBusy(false);
    }
  }, [api, doc, onAfterAction]);

  const handleTradeSell = useCallback(async (symbol) => {
    if (!doc) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/api/structures/${doc.id}/trade-sell`, { symbol: symbol ?? null });
      toast.success(`Sold for ${Math.round(data?.total_paid || 0)} cr`);
      onAfterAction?.('trade-sell');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sale failed');
    } finally {
      setBusy(false);
    }
  }, [api, doc, onAfterAction]);

  const handleRecycle = useCallback(async () => {
    if (!doc) return;
    setBusy(true);
    try {
      await api.delete(`/api/structures/${doc.id}`);
      toast.success('Structure recycled');
      onAfterAction?.('recycle', { id: doc.id });
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Recycle failed');
    } finally {
      setBusy(false);
    }
  }, [api, doc, onAfterAction, onClose]);

  const playerOres = inventory?.ores || [];
  const crateOres = doc?.data?.ores || [];
  const crateUsed = useMemo(() => crateOres.reduce((a, o) => a + (o.qty || 0), 0), [crateOres]);

  if (!open || !doc) return null;
  const Icon = TYPE_ICON[doc.type] || Sparkles;

  return (
    <div
      className="fixed inset-0 z-[2150] flex items-center justify-center"
      onClick={onClose}
      data-testid="structure-overlay"
    >
      <div className="absolute inset-0 bg-[#02060f]/85 backdrop-blur-md" />
      <div
        className="relative w-[min(720px,96vw)] max-h-[90vh] overflow-y-auto rounded-2xl border border-slate-700/60 bg-[#040912]/95 shadow-[0_0_0_1px_rgba(148,163,184,0.18)] p-6"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-start justify-between mb-5 gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Icon className="w-7 h-7 text-amber-300" />
            <div className="min-w-0">
              <h2
                className="text-xl font-bold text-slate-100 truncate"
                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                data-testid="structure-overlay-title"
              >
                {TYPE_LABEL[doc.type] || doc.type}
              </h2>
              <p className="text-[10px] text-slate-500 tracking-[0.2em] uppercase mt-0.5">
                Yours · HP {Math.round(doc.hp)}/{Math.round(doc.max_hp)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleRecycle} disabled={busy} data-testid="structure-recycle-btn">
              <Trash2 className="w-4 h-4 mr-2" />
              Recycle
            </Button>
            <Button variant="outline" size="sm" onClick={onClose} data-testid="structure-overlay-close">
              <X className="w-4 h-4 mr-2" /> Close
            </Button>
          </div>
        </div>

        {/* Type-specific body */}
        {doc.type === 'beacon' && (
          <div className="rounded-lg border border-amber-300/20 bg-amber-900/10 p-5 text-slate-200">
            Beacon active. It pings on your radar from anywhere on this body so you can find your way back.
          </div>
        )}

        {(doc.type === 'storage_crate' || doc.type === 'cargo_silo') && (
          <CratePanel
            doc={doc}
            playerOres={playerOres}
            crateOres={crateOres}
            crateUsed={crateUsed}
            cap={doc.data?.capacity || 100}
            onDeposit={handleCrateDeposit}
            onWithdraw={handleCrateWithdraw}
            busy={busy}
          />
        )}

        {doc.type === 'auto_miner' && (
          <MinerPanel
            doc={doc}
            onCollect={handleMinerCollect}
            busy={busy}
          />
        )}

        {doc.type === 'refuel_pad' && (
          <RefuelPanel
            doc={doc}
            onRefuel={handleRefuel}
            busy={busy}
          />
        )}

        {(doc.type === 'turret' || doc.type === 'heavy_turret') && (
          <TurretPanel
            doc={doc}
            onRefill={handleTurretRefill}
            busy={busy}
          />
        )}

        {doc.type === 'oxygen_generator' && (
          <InfoPanel testId="oxygen-panel" color="sky">
            Oxygen Generator online. While you stand within{' '}
            <span className="text-sky-300 font-mono">{Math.round(doc.data?.radius || 16)}m</span>,
            your O₂ refills automatically — no need to return to your ship.
          </InfoPanel>
        )}

        {doc.type === 'power_generator' && (
          <PowerPanel doc={doc} onCollect={handlePowerCollect} busy={busy} />
        )}

        {doc.type === 'floodlight' && (
          <InfoPanel testId="floodlight-panel" color="amber">
            Floodlight Tower active. It casts light across your base so you can keep
            working through the night cycle.
          </InfoPanel>
        )}

        {doc.type === 'refinery' && (
          <RefineryPanel
            doc={doc}
            playerOres={playerOres}
            onDeposit={handleRefineDeposit}
            onCollect={handleRefineCollect}
            onOpenCrafting={onOpenCrafting}
            busy={busy}
          />
        )}

        {doc.type === 'trade_terminal' && (
          <TradeTerminalPanel
            doc={doc}
            playerOres={playerOres}
            onSell={handleTradeSell}
            api={api}
            onAlloySold={() => onAfterAction?.('trade-sell')}
            busy={busy}
          />
        )}

        {doc.type === 'habitat' && (
          <InfoPanel testId="habitat-panel" color="teal">
            Habitat Module pressurized. Step inside to shelter from the surface — your
            oxygen refills while you remain within the module.
          </InfoPanel>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Per-type panels
// ============================================================================

function CratePanel({ doc, playerOres, crateOres, crateUsed, cap, onDeposit, onWithdraw, busy }) {
  return (
    <div className="space-y-4" data-testid="crate-panel">
      <div className="flex items-center justify-between rounded-md border border-slate-800/60 bg-slate-900/40 px-4 py-3">
        <span className="text-sm text-slate-300">
          Crate: <span className="font-mono text-slate-100">{crateUsed.toFixed(1)} / {Number(cap).toFixed(0)} u</span>
        </span>
        <span className="text-xs text-slate-500">ID: {doc.id.substring(0, 8)}…</span>
      </div>

      <div>
        <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400 mb-2">In Crate</div>
        {crateOres.length === 0 ? (
          <div className="text-sm text-slate-500 px-3 py-4 text-center border border-dashed border-slate-700/60 rounded-md">Empty</div>
        ) : (
          <div className="space-y-2">
            {crateOres.map((o) => (
              <Row
                key={o.symbol}
                ore={o}
                actionLabel="Withdraw"
                actionIcon={<ArrowUp className="w-4 h-4 mr-1" />}
                onAction={(qty) => onWithdraw(o.symbol, qty)}
                busy={busy}
                testIdPrefix="crate-withdraw"
              />
            ))}
          </div>
        )}
      </div>

      <div>
        <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400 mb-2">Your Cargo</div>
        {playerOres.length === 0 ? (
          <div className="text-sm text-slate-500 px-3 py-4 text-center border border-dashed border-slate-700/60 rounded-md">No cargo</div>
        ) : (
          <div className="space-y-2">
            {playerOres.map((o) => (
              <Row
                key={o.symbol}
                ore={o}
                actionLabel="Deposit"
                actionIcon={<ArrowDown className="w-4 h-4 mr-1" />}
                onAction={(qty) => onDeposit(o.symbol, qty)}
                busy={busy}
                testIdPrefix="crate-deposit"
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ ore, actionLabel, actionIcon, onAction, busy, testIdPrefix }) {
  const max = Number(ore.qty || 0);
  const all = Math.max(0, Math.floor(max * 10) / 10);
  return (
    <div className="flex items-center justify-between rounded-md border border-slate-800/60 bg-slate-900/40 px-4 py-2">
      <div className="flex items-center gap-3 min-w-0">
        <Badge variant="outline" className="border-slate-600 text-slate-300 font-mono">{ore.symbol}</Badge>
        <span className="text-slate-200 truncate">{ore.name || ore.symbol}</span>
        <span className="text-xs text-slate-500 font-mono">{max.toFixed(1)} u</span>
      </div>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={() => onAction(Math.min(1, max))}
          disabled={busy || max <= 0}
          data-testid={`${testIdPrefix}-${ore.symbol}-1`}
        >
          {actionIcon}
          1u
        </Button>
        <Button
          size="sm"
          onClick={() => onAction(all)}
          disabled={busy || max <= 0}
          className="bg-amber-500/90 hover:bg-amber-400 text-slate-950 font-semibold disabled:bg-slate-700 disabled:text-slate-400"
          data-testid={`${testIdPrefix}-${ore.symbol}-all`}
        >
          {actionLabel} All
        </Button>
      </div>
    </div>
  );
}

function MinerPanel({ doc, onCollect, busy }) {
  const buf = Number(doc.data?.buffer || 0);
  const cap = Number(doc.data?.max_buffer || 25);
  const ypm = Number(doc.data?.yield_per_minute || 2);
  const pct = cap > 0 ? Math.min(100, (buf / cap) * 100) : 0;
  return (
    <div className="space-y-4" data-testid="miner-panel">
      <div className="rounded-md border border-slate-800/60 bg-slate-900/40 px-4 py-3">
        <div className="flex items-center justify-between text-sm text-slate-300 mb-1">
          <span>Buffer</span>
          <span className="font-mono">{buf.toFixed(1)} / {cap.toFixed(0)} u</span>
        </div>
        <Progress value={pct} className="h-2 bg-slate-900/80" />
        <p className="text-xs text-slate-500 mt-2">
          Rate: {ypm.toFixed(1)}u / minute · buffer accrues passively. Visit periodically to collect.
        </p>
      </div>
      <div className="flex justify-end">
        <Button
          onClick={onCollect}
          disabled={busy}
          className="bg-cyan-500/90 hover:bg-cyan-400 text-slate-950 font-semibold"
          data-testid="miner-collect-btn"
        >
          <Pickaxe className="w-4 h-4 mr-2" />
          Collect Yield
        </Button>
      </div>
    </div>
  );
}

function RefuelPanel({ doc, onRefuel, busy }) {
  const cost = Number(doc.data?.use_cost_nanites || 3);
  return (
    <div className="space-y-4" data-testid="refuel-panel">
      <div className="rounded-md border border-slate-800/60 bg-slate-900/40 px-4 py-3 text-slate-300">
        Restore HP, shield, and oxygen at this pad. Each use consumes <span className="text-amber-200 font-mono">{cost} nanites</span>.
      </div>
      <div className="flex justify-end">
        <Button
          onClick={onRefuel}
          disabled={busy}
          className="bg-emerald-500/90 hover:bg-emerald-400 text-slate-950 font-semibold"
          data-testid="refuel-use-btn"
        >
          <BatteryCharging className="w-4 h-4 mr-2" />
          Refit Now
        </Button>
      </div>
    </div>
  );
}

function TurretPanel({ doc, onRefill, busy }) {
  const mag = Number(doc.data?.magazine || 0);
  const maxMag = Number(doc.data?.max_magazine || 40);
  const cost = Number(doc.data?.refill_cost_nanites || 4);
  const pct = maxMag > 0 ? (mag / maxMag) * 100 : 0;
  return (
    <div className="space-y-4" data-testid="turret-panel">
      <div className="rounded-md border border-slate-800/60 bg-slate-900/40 px-4 py-3">
        <div className="flex items-center justify-between text-sm text-slate-300 mb-1">
          <span>Magazine</span>
          <span className="font-mono">{mag} / {maxMag}</span>
        </div>
        <Progress value={pct} className="h-2 bg-slate-900/80" />
        <p className="text-xs text-slate-500 mt-2">
          Refill cost: <span className="text-amber-200 font-mono">{cost} nanites</span>
        </p>
      </div>
      <div className="flex justify-end">
        <Button
          onClick={onRefill}
          disabled={busy || mag >= maxMag}
          className="bg-amber-500/90 hover:bg-amber-400 text-slate-950 font-semibold disabled:bg-slate-700 disabled:text-slate-400"
          data-testid="turret-refill-btn"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refill Magazine
        </Button>
      </div>
    </div>
  );
}

const COLOR_MAP = {
  sky: 'border-sky-300/20 bg-sky-900/10',
  amber: 'border-amber-300/20 bg-amber-900/10',
  teal: 'border-teal-300/20 bg-teal-900/10',
};

function InfoPanel({ children, color = 'sky', testId }) {
  return (
    <div className={`rounded-lg border ${COLOR_MAP[color] || COLOR_MAP.sky} p-5 text-slate-200`} data-testid={testId}>
      {children}
    </div>
  );
}

function PowerPanel({ doc, onCollect, busy }) {
  const buf = Number(doc.data?.buffer || 0);
  const cap = Number(doc.data?.max_buffer || 30);
  const npm = Number(doc.data?.nanites_per_minute || 1);
  const pct = cap > 0 ? Math.min(100, (buf / cap) * 100) : 0;
  return (
    <div className="space-y-4" data-testid="power-panel">
      <div className="rounded-md border border-slate-800/60 bg-slate-900/40 px-4 py-3">
        <div className="flex items-center justify-between text-sm text-slate-300 mb-1">
          <span>Stored charge</span>
          <span className="font-mono">{buf.toFixed(1)} / {cap.toFixed(0)} nanites</span>
        </div>
        <Progress value={pct} className="h-2 bg-slate-900/80" />
        <p className="text-xs text-slate-500 mt-2">
          Generates {npm.toFixed(1)} nanites / minute passively. Collect on your return.
        </p>
      </div>
      <div className="flex justify-end">
        <Button
          onClick={onCollect}
          disabled={busy}
          className="bg-yellow-500/90 hover:bg-yellow-400 text-slate-950 font-semibold"
          data-testid="power-collect-btn"
        >
          <Zap className="w-4 h-4 mr-2" />
          Collect Nanites
        </Button>
      </div>
    </div>
  );
}

function RefineryPanel({ doc, playerOres, onDeposit, onCollect, onOpenCrafting, busy }) {
  const slots = doc.data?.slots || [];
  const cap = Number(doc.data?.capacity || 80);
  const bonus = Number(doc.data?.value_bonus || 1.5);
  const used = slots.reduce((a, s) => a + (s.qty || 0), 0);
  const now = Date.now() / 1000;
  const anyReady = slots.some((s) => Number(s.ready_ts || 0) <= now);
  return (
    <div className="space-y-4" data-testid="refinery-panel">
      {onOpenCrafting && (
        <button
          type="button"
          onClick={onOpenCrafting}
          data-testid="refinery-open-fabricator"
          className="w-full rounded-md border border-amber-400/40 bg-amber-400/10 hover:bg-amber-400/20 transition-colors px-4 py-3 text-left"
        >
          <div className="text-sm font-semibold text-amber-100">⚒ Fabricate Alloys</div>
          <div className="text-[11px] text-amber-200/70">Refine raw ore into multi-tier alloys (timed) — used for ship upgrades.</div>
        </button>
      )}
      <div className="flex items-center justify-between rounded-md border border-slate-800/60 bg-slate-900/40 px-4 py-3">
        <span className="text-sm text-slate-300">
          Refining: <span className="font-mono text-slate-100">{used.toFixed(1)} / {cap.toFixed(0)} u</span>
        </span>
        <span className="text-xs text-orange-300">+{Math.round((bonus - 1) * 100)}% value</span>
      </div>

      <div>
        <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400 mb-2">In Refinery</div>
        {slots.length === 0 ? (
          <div className="text-sm text-slate-500 px-3 py-4 text-center border border-dashed border-slate-700/60 rounded-md">Empty</div>
        ) : (
          <div className="space-y-2">
            {slots.map((s, i) => {
              const ready = Number(s.ready_ts || 0) <= now;
              const secsLeft = Math.max(0, Math.ceil(Number(s.ready_ts || 0) - now));
              return (
                <div key={`${s.symbol}-${i}`} className="flex items-center justify-between rounded-md border border-slate-800/60 bg-slate-900/40 px-4 py-2">
                  <div className="flex items-center gap-3 min-w-0">
                    <Badge variant="outline" className="border-slate-600 text-slate-300 font-mono">{s.symbol}</Badge>
                    <span className="text-slate-200 truncate">{s.name || s.symbol}</span>
                    <span className="text-xs text-slate-500 font-mono">{Number(s.qty || 0).toFixed(1)} u</span>
                  </div>
                  <span className={`text-xs font-mono ${ready ? 'text-emerald-300' : 'text-slate-400'}`}>
                    {ready ? 'Ready' : `${secsLeft}s`}
                  </span>
                </div>
              );
            })}
          </div>
        )}
        {slots.length > 0 && (
          <div className="flex justify-end mt-2">
            <Button
              onClick={onCollect}
              disabled={busy || !anyReady}
              className="bg-orange-500/90 hover:bg-orange-400 text-slate-950 font-semibold disabled:bg-slate-700 disabled:text-slate-400"
              data-testid="refinery-collect-btn"
            >
              <Factory className="w-4 h-4 mr-2" />
              Collect Refined
            </Button>
          </div>
        )}
      </div>

      <div>
        <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400 mb-2">Your Cargo</div>
        {playerOres.length === 0 ? (
          <div className="text-sm text-slate-500 px-3 py-4 text-center border border-dashed border-slate-700/60 rounded-md">No cargo</div>
        ) : (
          <div className="space-y-2">
            {playerOres.map((o) => (
              <Row
                key={o.symbol}
                ore={o}
                actionLabel="Refine"
                actionIcon={<ArrowDown className="w-4 h-4 mr-1" />}
                onAction={(qty) => onDeposit(o.symbol, qty)}
                busy={busy}
                testIdPrefix="refine-deposit"
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TradeTerminalPanel({ doc, playerOres, onSell, api, onAlloySold, busy }) {
  const fee = Number(doc.data?.fee_pct || 0.1);
  const total = playerOres.reduce((a, o) => a + (o.qty || 0) * (o.est_value_per_unit || 0), 0);
  const net = total * (1 - fee);
  return (
    <div className="space-y-4" data-testid="trade-terminal-panel">
      <div className="rounded-md border border-violet-300/20 bg-violet-900/10 px-4 py-3 text-slate-200 text-sm">
        Sell ore right here. A <span className="text-violet-300 font-mono">{Math.round(fee * 100)}%</span> surface surcharge applies versus city prices.
      </div>
      {playerOres.length === 0 ? (
        <div className="text-sm text-slate-500 px-3 py-4 text-center border border-dashed border-slate-700/60 rounded-md">No ore to sell</div>
      ) : (
        <>
          <div className="space-y-2">
            {playerOres.map((o) => (
              <div key={o.symbol} className="flex items-center justify-between rounded-md border border-slate-800/60 bg-slate-900/40 px-4 py-2">
                <div className="flex items-center gap-3 min-w-0">
                  <Badge variant="outline" className="border-slate-600 text-slate-300 font-mono">{o.symbol}</Badge>
                  <span className="text-slate-200 truncate">{o.name || o.symbol}</span>
                  <span className="text-xs text-slate-500 font-mono">{Number(o.qty || 0).toFixed(1)} u</span>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onSell(o.symbol)}
                  disabled={busy}
                  data-testid={`trade-sell-${o.symbol}`}
                >
                  Sell
                </Button>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between pt-2">
            <span className="text-sm text-slate-400">Est. payout: <span className="text-violet-200 font-mono">{Math.round(net)} cr</span></span>
            <Button
              onClick={() => onSell(null)}
              disabled={busy}
              className="bg-violet-500/90 hover:bg-violet-400 text-slate-950 font-semibold"
              data-testid="trade-sell-all-btn"
            >
              <Store className="w-4 h-4 mr-2" />
              Sell All
            </Button>
          </div>
        </>
      )}

      {/* Phase 2 — also sell refined alloys here (terminal fee applies) */}
      <AlloySellPanel
        api={api}
        terminal="structure"
        structureId={doc.id}
        accent="amber"
        onSold={onAlloySold}
      />
    </div>
  );
}

export default StructureInteractOverlay;